/**
 * Course Attempts registry — ops read-only view + manual add for edge cases.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { canonicalAttemptForStorage, formatDisplayDate, getCourseLabel } from '@fintree/utils'
import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/useAuth'
import { isSuperAdminRoleName } from '@/lib/authz'
import { ArrowLeft, Plus } from 'lucide-react'
import { useAddManualCourseAttempt } from '@/hooks/mutations/useCourseAttemptMutations'
import { useCourseAttemptsAll } from '@/hooks/queries/useStaffQueries'

export function CourseAttemptsSettingsPage() {
  const { identity } = useAuth()
  const isSuperAdmin = isSuperAdminRoleName(identity?.roleName)
  const isSupport = identity?.roleName === 'support'
  const canAccess = isSuperAdmin || isSupport
  const canAddManual = canAccess

  const [manualCourse, setManualCourse] = useState('CDP')
  const [manualAttempt, setManualAttempt] = useState('')

  const query = useCourseAttemptsAll()

  const addMut = useAddManualCourseAttempt({
    onSuccess: () => setManualAttempt(''),
  })

  if (!canAccess) {
    return (
      <div className="rounded-lg border border-destructive/ bg-destructive/ p-4 text-sm text-destructive">
        Support or super admin access required.
      </div>
    )
  }

  const rows = query.data ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/settings">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Settings
          </Link>
        </Button>
      </div>

      <PageHeader
        title="Course Attempts"
        subtitle="Canonical exam attempts synced from website catalog, enrollments, and student records."
      />

      {isSuperAdmin ? (
        <p className="text-sm text-muted-foreground">
          Catalog sync and registry refresh:{' '}
          <Link to="/admin/website-sync" className="text-primary underline">
            Website Sync Console
          </Link>
        </p>
      ) : null}

      {canAddManual ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Add manual attempt</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label>Course code</Label>
              <Input value={manualCourse} onChange={(e) => setManualCourse(e.target.value)} className="w-40" />
            </div>
            <div className="space-y-1">
              <Label>Attempt (e.g. Aug_28)</Label>
              <Input
                value={manualAttempt}
                onChange={(e) => setManualAttempt(e.target.value)}
                placeholder="Aug_28"
                className="w-40"
              />
            </div>
            <Button
              type="button"
              disabled={!manualAttempt.trim() || addMut.isPending}
              onClick={() => addMut.mutate({ course: manualCourse, attempt: manualAttempt })}
              className="bg-primary text-white hover:bg-fintree-navy"
            >
              <Plus className="mr-1 h-4 w-4" />
              Add
            </Button>
            {manualAttempt.trim() ? (
              <p className="text-xs text-muted-foreground">
                Stored as {canonicalAttemptForStorage(manualAttempt)}
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Active attempts ({rows.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <LoadingSpinner />
          ) : query.isError ? (
            <p className="text-sm text-destructive">Failed to load course attempts registry.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Course</TableHead>
                    <TableHead>Attempt</TableHead>
                    <TableHead>Display</TableHead>
                    <TableHead>Sources</TableHead>
                    <TableHead>Students</TableHead>
                    <TableHead>Catalog seen</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                        No active attempts in registry.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={`${row.course}-${row.attempt}`}>
                        <TableCell>
                          <span className="font-medium">{getCourseLabel(row.course)}</span>
                          <span className="ml-1 text-xs text-muted-foreground">({row.course})</span>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{row.attempt}</TableCell>
                        <TableCell>{row.displayAttempt}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {row.sources.map((s) => (
                              <Badge key={s} variant="outline" className="text-xs">
                                {s}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>{row.studentCount}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDisplayDate(row.catalogSeenAt)}
                        </TableCell>
                        <TableCell>
                          {row.isActive ? (
                            <Badge className="bg-fintree-emerald text-white">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
